import { toast } from 'sonner';
import { concatenateRecordingBlobs } from './concatenateRecordingBlobs';
import { InterviewLanguage } from './uploadAudio';

export type BrowserLocalRecordingType = 'conversation' | 'voice-over';

export interface BrowserLocalRecordingRecoveryMetadata {
  conversationId: string;
  recordingType: BrowserLocalRecordingType;
  mimeType: string;
  startedAt: string;
  updatedAt: string;
  expiresAt: string;
  interviewLanguage?: InterviewLanguage;
  // Audio captured so far across every session of this recording, pauses
  // excluded. Stored because the case list cannot decode blobs to find out.
  durationMs?: number;
}

// One record per recording session. Within a session the blob is replaced in
// place (keyed by sessionId); a continued recovery appends a new session, so a
// recovery holds at most one complete blob per session.
export interface BrowserLocalRecordingRecoverySessionRecord {
  id: string;
  recoveryKey: string;
  sessionId: string;
  conversationId: string;
  recordingType: BrowserLocalRecordingType;
  blob: Blob;
  createdAt: string;
  expiresAt: string;
}

export type BrowserLocalRecordingRecoveryMetadataRecord = {
  recoveryKey: string;
  metadata: BrowserLocalRecordingRecoveryMetadata;
};

export interface BrowserLocalRecordingRecoveryDatabase {
  putMetadata(recoveryKey: string, metadata: BrowserLocalRecordingRecoveryMetadata): Promise<void>;
  putSession(sessionRecord: BrowserLocalRecordingRecoverySessionRecord): Promise<void>;
  getMetadata(recoveryKey: string): Promise<BrowserLocalRecordingRecoveryMetadata | undefined>;
  getSessions(recoveryKey: string): Promise<BrowserLocalRecordingRecoverySessionRecord[]>;
  getAllMetadata(): Promise<BrowserLocalRecordingRecoveryMetadataRecord[]>;
  deleteRecovery(recoveryKey: string): Promise<void>;
}

export type BrowserLocalRecordingRecoveryRestoreResult =
  | {
      status: 'available';
      metadata: BrowserLocalRecordingRecoveryMetadata;
      blob: Blob;
    }
  | {
      status: 'unavailable';
      reason: 'missing' | 'expired' | 'corrupt';
    };

export type AvailableBrowserLocalRecordingRecovery = Extract<
  BrowserLocalRecordingRecoveryRestoreResult,
  { status: 'available' }
>;

export interface BrowserLocalRecordingRecoveryRepository {
  saveSnapshot(metadata: BrowserLocalRecordingRecoveryMetadata, blob: Blob, sessionId: string): Promise<void>;
  restore(
    conversationId: string,
    recordingType: BrowserLocalRecordingType,
    now?: Date
  ): Promise<BrowserLocalRecordingRecoveryRestoreResult>;
  findRecoverableRecording(
    recordingType: BrowserLocalRecordingType,
    now?: Date
  ): Promise<AvailableBrowserLocalRecordingRecovery | null>;
  listPendingRecordings(now?: Date): Promise<BrowserLocalRecordingRecoveryMetadata[]>;
  discard(conversationId: string, recordingType: BrowserLocalRecordingType): Promise<void>;
  cleanupExpired(now?: Date): Promise<void>;
}

export interface BrowserLocalRecordingRecovery {
  saveSnapshot(options: SaveBrowserLocalRecordingRecoverySnapshotOptions): void;
  flush(conversationId: string, recordingType: BrowserLocalRecordingType): Promise<void>;
  restore(
    conversationId: string,
    recordingType: BrowserLocalRecordingType
  ): Promise<BrowserLocalRecordingRecoveryRestoreResult>;
  findRecoverableRecording(recordingType: BrowserLocalRecordingType): Promise<AvailableBrowserLocalRecordingRecovery | null>;
  listPendingRecordings(): Promise<BrowserLocalRecordingRecoveryMetadata[]>;
  discard(conversationId: string, recordingType: BrowserLocalRecordingType): Promise<void>;
  prepareForFreshRecording(conversationId: string, recordingType: BrowserLocalRecordingType): Promise<void>;
  cleanupExpired(): Promise<void>;
}

export interface SaveBrowserLocalRecordingRecoverySnapshotOptions {
  conversationId: string;
  recordingType: BrowserLocalRecordingType;
  mimeType: string;
  startedAt: string;
  // Identifies the recording session. Snapshots that share a sessionId replace
  // each other; a new session (continuation) appends another blob.
  sessionId: string;
  // The complete recording for this session from its start until now. Each
  // snapshot overwrites the previous one for the same session.
  blob: Blob;
  interviewLanguage?: InterviewLanguage;
  durationMs?: number;
}

interface CreateBrowserLocalRecordingRecoveryOptions {
  repository: BrowserLocalRecordingRecoveryRepository;
  now?: () => Date;
  onUnavailable?: (error: unknown) => void;
}

interface IndexedDbBrowserLocalRecordingRecoveryRepositoryOptions {
  openDatabase?: () => Promise<BrowserLocalRecordingRecoveryDatabase>;
  concatenateBlobs?: (blobs: Blob[]) => Promise<Blob>;
}

const DATABASE_NAME = 'gesprekskompas-browser-local-recording-recovery';
const DATABASE_VERSION = 3;
const METADATA_STORE = 'recordingRecoveryMetadata';
const SESSION_STORE = 'recordingRecoverySessions';
const LEGACY_CHUNKS_STORE = 'recordingRecoveryChunks';
const LEGACY_SNAPSHOT_STORE = 'recordingRecoverySnapshots';
const BROWSER_LOCAL_RECOVERY_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export const BROWSER_LOCAL_RECOVERY_SNAPSHOT_INTERVAL_MS = 1000;

export const browserLocalRecordingRecoveryKey = (
  conversationId: string,
  recordingType: BrowserLocalRecordingType
): string => `${conversationId}::${recordingType}`;

const sessionRecordId = (recoveryKey: string, sessionId: string): string => `${recoveryKey}::${sessionId}`;

const transactionDone = (transaction: IDBTransaction): Promise<void> => new Promise((resolve, reject) => {
  transaction.oncomplete = () => resolve();
  transaction.onerror = () => reject(transaction.error ?? new Error('IndexedDB transaction failed'));
  transaction.onabort = () => reject(transaction.error ?? new Error('IndexedDB transaction aborted'));
});

const openIndexedDbDatabase = (): Promise<IDBDatabase> => new Promise((resolve, reject) => {
  if (!window.indexedDB) {
    reject(new Error('IndexedDB is not available'));
    return;
  }

  const request = window.indexedDB.open(DATABASE_NAME, DATABASE_VERSION);

  request.onupgradeneeded = () => {
    const database = request.result;
    if (!database.objectStoreNames.contains(METADATA_STORE)) {
      database.createObjectStore(METADATA_STORE);
    }
    // Older chunk/snapshot schemas cannot be reassembled by the per-session
    // model, and recoveries are short-lived crash state, so drop them.
    [LEGACY_CHUNKS_STORE, LEGACY_SNAPSHOT_STORE, SESSION_STORE].forEach((storeName) => {
      if (database.objectStoreNames.contains(storeName)) {
        database.deleteObjectStore(storeName);
      }
    });
    const sessions = database.createObjectStore(SESSION_STORE, { keyPath: 'id' });
    sessions.createIndex('recoveryKey', 'recoveryKey', { unique: false });
  };
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error ?? new Error('Could not open IndexedDB'));
});

export const putBrowserLocalRecordingRecoveryMetadata = (
  metadataStore: Pick<IDBObjectStore, 'keyPath' | 'put'>,
  recoveryKey: string,
  metadata: BrowserLocalRecordingRecoveryMetadata
): IDBRequest<IDBValidKey> => {
  if (metadataStore.keyPath) {
    return metadataStore.put({ ...metadata, recoveryKey });
  }

  return metadataStore.put(metadata, recoveryKey);
};

const defaultOpenDatabase = async (): Promise<BrowserLocalRecordingRecoveryDatabase> => {
  const database = await openIndexedDbDatabase();

  return {
    async putMetadata(recoveryKey, metadata) {
      const transaction = database.transaction([METADATA_STORE], 'readwrite');
      putBrowserLocalRecordingRecoveryMetadata(transaction.objectStore(METADATA_STORE), recoveryKey, metadata);
      await transactionDone(transaction);
    },
    async putSession(sessionRecord) {
      const transaction = database.transaction([SESSION_STORE], 'readwrite');
      transaction.objectStore(SESSION_STORE).put(sessionRecord);
      await transactionDone(transaction);
    },
    async getMetadata(recoveryKey) {
      const transaction = database.transaction([METADATA_STORE], 'readonly');
      const request = transaction.objectStore(METADATA_STORE).get(recoveryKey);
      await transactionDone(transaction);
      return request.result as BrowserLocalRecordingRecoveryMetadata | undefined;
    },
    async getSessions(recoveryKey) {
      const transaction = database.transaction([SESSION_STORE], 'readonly');
      const request = transaction.objectStore(SESSION_STORE).index('recoveryKey').getAll(recoveryKey);
      await transactionDone(transaction);
      return (request.result as BrowserLocalRecordingRecoverySessionRecord[]).sort(
        (left, right) => left.sessionId.localeCompare(right.sessionId)
      );
    },
    async getAllMetadata() {
      const transaction = database.transaction([METADATA_STORE], 'readonly');
      const metadataStore = transaction.objectStore(METADATA_STORE);
      const records: BrowserLocalRecordingRecoveryMetadataRecord[] = [];
      const request = metadataStore.openCursor();
      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          records.push({
            recoveryKey: String(cursor.key),
            metadata: cursor.value as BrowserLocalRecordingRecoveryMetadata,
          });
          cursor.continue();
        }
      };
      await transactionDone(transaction);
      return records;
    },
    async deleteRecovery(recoveryKey) {
      const transaction = database.transaction([METADATA_STORE, SESSION_STORE], 'readwrite');
      transaction.objectStore(METADATA_STORE).delete(recoveryKey);
      const sessionStore = transaction.objectStore(SESSION_STORE);
      const request = sessionStore.index('recoveryKey').openKeyCursor(IDBKeyRange.only(recoveryKey));
      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          sessionStore.delete(cursor.primaryKey);
          cursor.continue();
        }
      };
      await transactionDone(transaction);
    },
  };
};

export class IndexedDbBrowserLocalRecordingRecoveryRepository implements BrowserLocalRecordingRecoveryRepository {
  private openDatabase: () => Promise<BrowserLocalRecordingRecoveryDatabase>;
  private concatenateBlobs: (blobs: Blob[]) => Promise<Blob>;

  constructor(options: IndexedDbBrowserLocalRecordingRecoveryRepositoryOptions = {}) {
    this.openDatabase = options.openDatabase ?? defaultOpenDatabase;
    this.concatenateBlobs = options.concatenateBlobs ?? concatenateRecordingBlobs;
  }

  async saveSnapshot(
    metadata: BrowserLocalRecordingRecoveryMetadata,
    blob: Blob,
    sessionId: string
  ): Promise<void> {
    const database = await this.openDatabase();
    const recoveryKey = browserLocalRecordingRecoveryKey(metadata.conversationId, metadata.recordingType);

    await database.putMetadata(recoveryKey, metadata);
    await database.putSession(buildSessionRecord(metadata, blob, sessionId));
  }

  async restore(
    conversationId: string,
    recordingType: BrowserLocalRecordingType,
    now: Date = new Date()
  ): Promise<BrowserLocalRecordingRecoveryRestoreResult> {
    const database = await this.openDatabase();
    const recoveryKey = browserLocalRecordingRecoveryKey(conversationId, recordingType);
    const metadata = await database.getMetadata(recoveryKey);

    if (!metadata) {
      return { status: 'unavailable', reason: 'missing' };
    }

    if (isExpiredRecovery(metadata, now)) {
      await database.deleteRecovery(recoveryKey);
      return { status: 'unavailable', reason: 'expired' };
    }

    const sessions = await database.getSessions(recoveryKey);
    if (!isValidRecoverySessions(sessions, recoveryKey, conversationId, recordingType)) {
      await database.deleteRecovery(recoveryKey);
      return { status: 'unavailable', reason: 'corrupt' };
    }

    return {
      status: 'available',
      metadata,
      blob: await this.assembleRecoveredBlob(metadata, sessions),
    };
  }

  async findRecoverableRecording(
    recordingType: BrowserLocalRecordingType,
    now: Date = new Date()
  ): Promise<AvailableBrowserLocalRecordingRecovery | null> {
    const database = await this.openDatabase();
    const candidates = (await database.getAllMetadata())
      .filter(({ metadata }) => metadata.recordingType === recordingType)
      .sort((left, right) => new Date(right.metadata.updatedAt).getTime() - new Date(left.metadata.updatedAt).getTime());

    for (const { metadata } of candidates) {
      const restored = await this.restore(metadata.conversationId, recordingType, now);
      if (restored.status === 'available') {
        return restored;
      }
    }

    return null;
  }

  // Metadata only: the case list needs to know *that* a recording is pending,
  // not its audio, and assembling blobs there would decode every take.
  async listPendingRecordings(now: Date = new Date()): Promise<BrowserLocalRecordingRecoveryMetadata[]> {
    const database = await this.openDatabase();
    return (await database.getAllMetadata())
      .map(({ metadata }) => metadata)
      .filter((metadata) => !isExpiredRecovery(metadata, now))
      .sort(
        (left, right) =>
          new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime()
      );
  }

  async discard(conversationId: string, recordingType: BrowserLocalRecordingType): Promise<void> {
    const database = await this.openDatabase();
    await database.deleteRecovery(browserLocalRecordingRecoveryKey(conversationId, recordingType));
  }

  async cleanupExpired(now: Date = new Date()): Promise<void> {
    const database = await this.openDatabase();
    const recoveries = await database.getAllMetadata();
    await Promise.all(
      expiredRecoveryKeys(recoveries, now).map((recoveryKey) => database.deleteRecovery(recoveryKey))
    );
  }

  private async assembleRecoveredBlob(
    metadata: BrowserLocalRecordingRecoveryMetadata,
    sessions: BrowserLocalRecordingRecoverySessionRecord[]
  ): Promise<Blob> {
    if (sessions.length === 1) {
      return new Blob([sessions[0].blob], { type: metadata.mimeType });
    }

    return this.concatenateBlobs(sessions.map((session) => session.blob));
  }
}

const buildRecoveryMetadata = (
  options: SaveBrowserLocalRecordingRecoverySnapshotOptions,
  updatedAt: Date
): BrowserLocalRecordingRecoveryMetadata => ({
  conversationId: options.conversationId,
  recordingType: options.recordingType,
  mimeType: options.mimeType,
  startedAt: options.startedAt,
  updatedAt: updatedAt.toISOString(),
  expiresAt: new Date(updatedAt.getTime() + BROWSER_LOCAL_RECOVERY_TTL_MS).toISOString(),
  ...(options.recordingType === 'conversation' && options.interviewLanguage
    ? { interviewLanguage: options.interviewLanguage }
    : {}),
  ...(typeof options.durationMs === 'number' ? { durationMs: options.durationMs } : {}),
});

const isExpiredRecovery = (metadata: BrowserLocalRecordingRecoveryMetadata, now: Date): boolean => (
  new Date(metadata.expiresAt).getTime() <= now.getTime()
);

const expiredRecoveryKeys = (
  recoveries: BrowserLocalRecordingRecoveryMetadataRecord[],
  now: Date
): string[] => recoveries
  .filter(({ metadata }) => isExpiredRecovery(metadata, now))
  .map(({ recoveryKey }) => recoveryKey);

const buildSessionRecord = (
  metadata: BrowserLocalRecordingRecoveryMetadata,
  blob: Blob,
  sessionId: string
): BrowserLocalRecordingRecoverySessionRecord => {
  const recoveryKey = browserLocalRecordingRecoveryKey(metadata.conversationId, metadata.recordingType);

  return {
    id: sessionRecordId(recoveryKey, sessionId),
    recoveryKey,
    sessionId,
    conversationId: metadata.conversationId,
    recordingType: metadata.recordingType,
    blob,
    createdAt: metadata.updatedAt,
    expiresAt: metadata.expiresAt,
  };
};

const isValidRecoverySessions = (
  sessions: BrowserLocalRecordingRecoverySessionRecord[],
  recoveryKey: string,
  conversationId: string,
  recordingType: BrowserLocalRecordingType
): boolean => (
  sessions.length > 0 && sessions.every((session) => (
    session.recoveryKey === recoveryKey &&
    session.conversationId === conversationId &&
    session.recordingType === recordingType &&
    session.blob instanceof Blob &&
    session.blob.size > 0
  ))
);

const freshRecordingRecoveryTypes = (
  recordingType: BrowserLocalRecordingType
): BrowserLocalRecordingType[] => (
  recordingType === 'conversation' ? ['conversation', 'voice-over'] : ['voice-over']
);

class BrowserLocalRecordingRecoveryWriteQueue {
  private queuedWrites = new Map<string, Promise<void>>();

  constructor(
    private repository: BrowserLocalRecordingRecoveryRepository,
    private onUnavailable: (error: unknown) => void
  ) {}

  saveSnapshot(metadata: BrowserLocalRecordingRecoveryMetadata, blob: Blob, sessionId: string): void {
    const recoveryKey = browserLocalRecordingRecoveryKey(metadata.conversationId, metadata.recordingType);
    const queuedWrite = this.appendWrite(recoveryKey, () => this.repository.saveSnapshot(metadata, blob, sessionId));

    this.queuedWrites.set(recoveryKey, queuedWrite);
  }

  async flush(recoveryKey: string): Promise<void> {
    await this.queuedWrites.get(recoveryKey);
  }

  private appendWrite(recoveryKey: string, write: () => Promise<void>): Promise<void> {
    const previousWrite = this.queuedWrites.get(recoveryKey) ?? Promise.resolve();

    return previousWrite
      .catch(() => undefined)
      .then(write)
      .catch((error) => {
        this.onUnavailable(error);
      });
  }
}

export const createBrowserLocalRecordingRecovery = ({
  repository,
  now = () => new Date(),
  onUnavailable = () => undefined,
}: CreateBrowserLocalRecordingRecoveryOptions): BrowserLocalRecordingRecovery => {
  const writeQueue = new BrowserLocalRecordingRecoveryWriteQueue(repository, onUnavailable);
  const flushRecovery = (conversationId: string, recordingType: BrowserLocalRecordingType): Promise<void> => (
    writeQueue.flush(browserLocalRecordingRecoveryKey(conversationId, recordingType))
  );
  const discardAfterQueuedWrites = async (
    conversationId: string,
    recordingType: BrowserLocalRecordingType
  ): Promise<void> => {
    const recoveryKey = browserLocalRecordingRecoveryKey(conversationId, recordingType);
    await writeQueue.flush(recoveryKey);
    await repository.discard(conversationId, recordingType);
  };

  return {
    saveSnapshot(options) {
      writeQueue.saveSnapshot(buildRecoveryMetadata(options, now()), options.blob, options.sessionId);
    },
    async flush(conversationId, recordingType) {
      await flushRecovery(conversationId, recordingType);
    },
    async restore(conversationId, recordingType) {
      return repository.restore(conversationId, recordingType, now());
    },
    async findRecoverableRecording(recordingType) {
      return repository.findRecoverableRecording(recordingType, now());
    },
    async listPendingRecordings() {
      return repository.listPendingRecordings(now());
    },
    async discard(conversationId, recordingType) {
      await discardAfterQueuedWrites(conversationId, recordingType);
    },
    async prepareForFreshRecording(conversationId, recordingType) {
      await Promise.all(freshRecordingRecoveryTypes(recordingType).map((type) => (
        discardAfterQueuedWrites(conversationId, type)
      )));
    },
    async cleanupExpired() {
      await repository.cleanupExpired(now());
    },
  };
};

let hasWarnedAboutUnavailableRecovery = false;

export const defaultBrowserLocalRecordingRecovery = createBrowserLocalRecordingRecovery({
  repository: new IndexedDbBrowserLocalRecordingRecoveryRepository(),
  onUnavailable: (error) => {
    console.warn('Browser-local recording recovery is unavailable:', error);
    if (!hasWarnedAboutUnavailableRecovery) {
      hasWarnedAboutUnavailableRecovery = true;
      toast.warning('Crashherstel voor deze opname is niet beschikbaar op dit apparaat. De opname loopt gewoon door.');
    }
  },
});
